import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldArray, Formik, FormikProvider, useFormik } from 'formik';
import PropTypes from 'prop-types';
import { boolean } from 'yup';
import {
  Alert,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Chip,
  useMediaQuery,
  FormControl,
} from '@mui/material';
import { IconSearch, IconFileExport } from '@tabler/icons-react';
import { Button, Input, Select, IconButton } from '@atoms';
import {
  useCategoriesByCustomer,
  useExportAllUserAccountsFiltered,
  useFilterInternalFeatures,
  useProductsByCustomer,
  useUser,
  usePartnerCustomers,
  useCustomerId,
  useShowAreaFeature,
  useIsAreasAdmin,
} from '@hooks';
import { SelectAreas } from '@organisms';
import {
  CATEGORIES_PARSE_OPTIONS,
  FIELDS,
  PRODUCT_PARSE_OPTIONS,
  cuilValidationSchema,
  validationSchema,
} from './constants';
import styles from './styles';
import {
  employeeListClick,
  employeeListFilterMovements,
  EVENT_EMPLOYEE_LIST_CLEAR_FILTER_ACCOUNTS,
  EVENT_EMPLOYEE_LIST_EXPORT_ACCOUNTS,
  EVENT_EMPLOYEE_LIST_MULTIPLE_CUIL,
} from '../events';

/**
 * Renders a modal with the inputs fields and search clients
 * @param {object} props
 * @param  {boolean} props.isLoading flag indicating whether the request is loading
 * @param  {object} props.params the current URL params
 * @param  {boolean} props.disabledExportAll flag indicating whether the export all button should be disabled
 * @param  {() => void | () => Promise<void>} props.onFiltersFormSubmit function to set clients data
 */
const SearchBoxClient = ({ isLoading, params, disabledExportAll, onFiltersFormSubmit }) => {
  const { user } = useUser();
  const { t } = useTranslation();
  const fields = useFilterInternalFeatures(FIELDS);
  const { data: customersData } = usePartnerCustomers();
  const [isMultipleSearching, setIsMultipleSearching] = useState(
    Boolean(params?.cuit?.includes(','))
  );
  const responsive = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const [invalidCuils, setInvalidCuils] = useState([]);
  const [excludedCuils, setExcludedCuils] = useState([]);

  const searchClientFormik = useFormik({
    initialValues: {
      cuit: getCuitInitialValue(),
      dni: params?.dni || '',
      firstName: params?.firstName || '',
      lastName: params?.lastName || '',
      walletId: params?.walletId || '',
      email: params?.email || '',
      product: params?.product || '',
      categories: params?.categories?.split(',') || [],
      customerId: params?.customerId || '',
      areaId: params?.areaId || '',
    },
    validationSchema: validationSchema({ user }),
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: onFiltersFormSubmit,
  });
  /**
   * get cuit initial value
   * @returns Array of cuit or string of one cuit
   */
  function getCuitInitialValue() {
    return params?.cuit?.includes(',') ? params.cuit.split(',') : params.cuit ?? '';
  }

  const {
    handleChange: formikHandleChange,
    handleSubmit,
    handleBlur,
    isValid,
    errors,
    values,
    setValues,
    resetForm,
    setFieldValue,
  } = searchClientFormik;

  const { showAreaFeature } = useShowAreaFeature();
  const { isAreasAdmin } = useIsAreasAdmin();
  const validateArea = showAreaFeature && !isAreasAdmin ? !values?.areaId : false;

  const cuilIsArray = values?.cuit instanceof Array;
  const { customerId } = useCustomerId();
  const selectedCustomerId = user?.isInternal ? values.customerId : customerId;

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useCategoriesByCustomer({
    customerId: selectedCustomerId,
    allowRequest: Boolean(selectedCustomerId),
  });

  const {
    data: productsData,
    isLoading: isProductsLoading,
    error: productsError,
  } = useProductsByCustomer({
    customerId: selectedCustomerId,
    allowRequest: Boolean(selectedCustomerId),
  });

  const canSelectCompany = user.isInternal;

  /** Clears the filters */
  function handleClearFilters() {
    resetForm();
    onFiltersFormSubmit({});
    setInvalidCuils([]);
    setExcludedCuils([]);
  }

  /**
   * Handles the form change
   * Trims whitespaces at the beginning and end if the event is a clipboard paste
   */
  function handleChange(name) {
    const handler = formikHandleChange(name);
    return (event) => {
      if (event.type === 'paste') {
        event.preventDefault();
        handler(event.clipboardData.getData('text/plain').trim());
      } else {
        handler(event.target.value);
      }
    };
  }
  /**
   * Cuit multiple search handle key down event
   * @param event Handle key down event
   * @param _props Formik props
   */
  const handleCUILAdd = (rawInput, inputProps) => {
    const VALID_CUIL_REGEX = /^\d{11}$/;

    const entries = rawInput
      .split(/[\s,;,\n,\r]+/)
      .map((v) => v.trim())
      .filter(Boolean);

    const validCUILs = entries.filter((v) => VALID_CUIL_REGEX.test(v));
    const invalidCUILs = entries.filter((v) => !VALID_CUIL_REGEX.test(v));

    const currentList = Array.isArray(values.cuit) ? values.cuit : [];

    const combinedList = [...currentList, ...validCUILs].filter(
      (v, i, arr) => arr.indexOf(v) === i
    );

    const limitedList = combinedList.slice(0, 25);
    const excludedCuils = combinedList.slice(25);
    searchClientFormik.setFieldValue('cuit', limitedList);
    setInvalidCuils(invalidCUILs);
    setExcludedCuils(excludedCuils);
    inputProps.setFieldValue('cuit', '');
  };

  /**
   * Handles keydown events to add CUIL when Enter or comma is pressed
   *
   * @param {KeyboardEvent} event - Keydown event
   * @param {Object} inputProps - Additional input props for handleCUILAdd
   */
  const cuilHandleKeyDown = (event, inputProps) => {
    const value = event.target.value.trim();
    if ((event.key === 'Enter' || event.key === ',') && value) {
      event.preventDefault();
      handleCUILAdd(value, inputProps);
    }
  };

  /**
   * Handles paste events to extract and sanitize CUIL values from clipboard
   *
   * @param {ClipboardEvent} event - Paste event
   * @param {Object} inputProps - Additional input props for handleCUILAdd
   */
  const cuilHandlePaste = (event, inputProps) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    const sanitized = pasted.replace(/[^\d\s,;\n\r]/g, '');
    if (sanitized) {
      handleCUILAdd(sanitized, inputProps);
    }
  };
  const disableButton = isLoading || !isValid || validateArea;
  const maxCuilMultipleSearch = cuilIsArray && values?.cuit?.length >= 25;

  const hasSelectedACompany = Boolean(user?.isInternal ? values.customerId : customerId);

  const { isLoading: isLoadingExportAll, exportAllUserAccountsFiltered } =
    useExportAllUserAccountsFiltered();

  /**
   * Updates the selected company and clears filters that depend on it
   * @param {object} event select change event
   */
  function handleCompanyChange(event) {
    formikHandleChange('customerId')(event);
    setFieldValue('product', '');
    setFieldValue('categories', []);
  }

  /**
   *  On change of multiple search checkbox
   * @param event checkbox on change event
   */
  function checkboxMultipleSearchHandleChange(event) {
    employeeListClick(EVENT_EMPLOYEE_LIST_MULTIPLE_CUIL);
    handleClearFilters();
    setFieldValue('cuit', event.target.checked ? [] : '');
    setIsMultipleSearching(event.target.checked);

    if (!event.target.checked) {
      setInvalidCuils([]);
      setExcludedCuils([]);
    }
  }

  /**
   * Normalize array and string errors
   * @param {Array | string } error error to normalize
   */
  function normalizeError(error) {
    return Array.isArray(error) ? error.filter((n) => n)[0] : error;
  }

  /** Handles exporting dispersion data.*/
  const handleExportAccounts = () => {
    employeeListClick(EVENT_EMPLOYEE_LIST_EXPORT_ACCOUNTS);
    exportAllUserAccountsFiltered({
      customerId: params.customerId,
      lastName: params.lastName,
      firstName: params.firstName,
      email: params.email,
      cuit: params.cuit,
      walletId: params.walletId,
      product: params.product,
      categories: params.categories,
      dni: params.dni,
      areaId: params.areaId,
    });
  };

  useEffect(() => {
    if (values.customerId) {
      setValues((prevValues) => {
        if (prevValues.customerId == values.customerId) {
          return prevValues;
        }
        return {
          cuit: '',
          dni: '',
          firstName: '',
          lastName: '',
          walletId: '',
          email: '',
          product: '',
          categories: [],
          customerId: prevValues.customerId,
          areaId: '',
        };
      });
    }
  }, [values.customerId, setValues]);

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('EMPLOYEES:SEARCH_CLIENT')}</Typography>
      </Box>
      <Box style={styles.content}>
        <Box style={styles.firstLine(responsive)}>
          <Box style={styles.inputsContainer}>
            {!isMultipleSearching ? (
              <Box style={styles.inputGrid}>
                {fields.map(({ name, label }) => (
                  <Input
                    key={name}
                    label={label}
                    variant='outlined'
                    size='small'
                    name={name}
                    value={values[name]}
                    onChange={handleChange(name)}
                    inputProps={{ onPaste: handleChange(name) }}
                    onBlur={handleBlur(name)}
                    helperText={errors[name]}
                    error={errors[name]}
                    fullWidth
                    sx={{ maxWidth: !responsive ? '143px' : '100%' }}
                  />
                ))}
              </Box>
            ) : (
              <Box style={styles.searchContainerCuil}>
                <Formik
                  initialValues={{ cuit: '' }}
                  validationSchema={cuilValidationSchema(values)}
                >
                  {(inputProps) => (
                    <Box style={styles.inputCuilContainer(responsive)}>
                      <Box style={styles.firstLineCuilContainer}>
                        <Input
                          name='cuit'
                          value={inputProps.values['cuit']}
                          label={'COMMON:CUIL'}
                          onChange={inputProps.handleChange}
                          onKeyDown={(e) => cuilHandleKeyDown(e, inputProps)}
                          onPaste={(e) => cuilHandlePaste(e, inputProps)}
                          onBlur={inputProps.handleBlur}
                          helperText={inputProps.errors['cuit'] ?? ' '}
                          error={inputProps.errors['cuit']}
                          disabled={maxCuilMultipleSearch}
                          sx={styles.inputCuil(inputProps.errors['cuit'], responsive)}
                        />
                        <Box style={styles.infoCuil}>
                          {!maxCuilMultipleSearch && !errors.cuit && (
                            <Alert severity='info'>{t('EMPLOYEES:MULTIPLE_SEARCH_TOOLTIP')}</Alert>
                          )}
                          {maxCuilMultipleSearch && !errors.cuit && (
                            <Alert severity='info'>{t('EMPLOYEES:MULTIPLE_SEARCH_MAX')}</Alert>
                          )}
                          {errors.cuit && (
                            <Alert severity='info'>{t(normalizeError(errors.cuit))}</Alert>
                          )}
                        </Box>
                      </Box>
                      <Box style={styles.secondLineCuilContainer}>
                        {invalidCuils.length > 0 && (
                          <Alert severity='error'>
                            {t('EMPLOYEES:MULTIPLE_SEARCH_INVALID_CUILS', {
                              cuils: invalidCuils.join(', '),
                            })}
                          </Alert>
                        )}
                        {excludedCuils.length > 0 && (
                          <Alert severity='warning'>
                            {t('EMPLOYEES:MULTIPLE_SEARCH_EXCLUDED_CUILS_LIMIT', {
                              cuils: excludedCuils.join(', '),
                            })}
                          </Alert>
                        )}
                      </Box>
                    </Box>
                  )}
                </Formik>
              </Box>
            )}
          </Box>
          <Box style={styles.actionsContainer(responsive)}>
            <Button
              customStyle={styles.clearButton}
              text={t('COMMON:CLEAR_FILTER')}
              color='primary'
              variant='link'
              onClick={() => {
                employeeListClick(EVENT_EMPLOYEE_LIST_CLEAR_FILTER_ACCOUNTS);
                handleClearFilters();
                setFieldValue('cuit', isMultipleSearching ? [] : '');
              }}
            />
            <IconButton
              aria-label={t('COMMON:EXPORT')}
              tooltip='COMMON:EXPORT'
              onClick={handleExportAccounts}
              IconComponent={IconFileExport}
              isLoading={isLoadingExportAll}
              disabled={disabledExportAll}
            />
            <IconButton
              aria-label={t('COMMON:SEARCH')}
              tooltip='COMMON:SEARCH'
              onClick={() => {
                employeeListFilterMovements(values);
                handleSubmit();
              }}
              IconComponent={IconSearch}
              isLoading={isLoading}
              disabled={disableButton}
              variant='contained'
            />
          </Box>
        </Box>
        {isMultipleSearching && (
          <FormikProvider value={searchClientFormik}>
            <Box style={styles.secondLine(responsive)}>
              <Box style={styles.ChipContainer}>
                <FieldArray
                  name='cuit'
                  render={({ remove }) =>
                    cuilIsArray &&
                    values.cuit?.map((item, index) => (
                      <Chip
                        m={1}
                        key={index}
                        label={item}
                        onDelete={() => {
                          remove(index);
                        }}
                      />
                    ))
                  }
                />
              </Box>
            </Box>
          </FormikProvider>
        )}
        {!isMultipleSearching && (
          <Box style={styles.secondLine(responsive)}>
            {canSelectCompany && (
              <Select
                label={t('COMMON:COMPANY')}
                value={values.customerId}
                onChange={handleCompanyChange}
                error={errors.customerId}
                helperText={errors.customerId}
                items={customersData}
                placeholder='COMMON:SELECT_COMPANY'
                parseOption={(item) => ({
                  label: item.legalName,
                  value: String(item.id),
                })}
                customStyle={{ maxWidth: !responsive ? '180px' : '100%' }}
              />
            )}
            {showAreaFeature && (
              <FormControl fullWidth sx={{ maxWidth: !responsive ? '180px' : '100%' }}>
                <SelectAreas
                  value={values['areaId']}
                  onChange={(_event, value) => {
                    setFieldValue('areaId', value.id);
                  }}
                  customStyle={{ maxWidth: !responsive ? '180px' : '100%' }}
                  customerId={user.isInternal && values.customerId}
                  disabled={user.isInternal && !values.customerId}
                />
              </FormControl>
            )}
            <Select
              label={t('DISPERSIONS:FAST_DISPERSION:PRODUCT')}
              value={values.product}
              disabled={!hasSelectedACompany}
              onChange={formikHandleChange('product')}
              items={productsData || []}
              isLoading={isProductsLoading}
              parseOption={PRODUCT_PARSE_OPTIONS}
              error={errors.product}
              isError={Boolean(productsError)}
              customStyle={{ maxWidth: !responsive ? '180px' : '100%' }}
            />
            <Select
              name='categories'
              disabled={!hasSelectedACompany}
              label={t('DISPERSIONS:FAST_DISPERSION:CATEGORY')}
              value={values.categories}
              onChange={formikHandleChange('categories')}
              items={categoriesData || []}
              isLoading={isCategoriesLoading}
              parseOption={CATEGORIES_PARSE_OPTIONS}
              isError={Boolean(categoriesError)}
              multiple
              customStyle={{ maxWidth: !responsive ? '180px' : '100%' }}
            />
          </Box>
        )}
      </Box>
      <Box style={styles.footer}>
        <FormControlLabel
          required
          control={
            <Checkbox checked={isMultipleSearching} onChange={checkboxMultipleSearchHandleChange} />
          }
          label={t('EMPLOYEES:MULTIPLE_SEARCH')}
          sx={styles.checkboxInput}
        />
      </Box>
    </Box>
  );
};

export default SearchBoxClient;

SearchBoxClient.propTypes = {
  isLoading: PropTypes.bool,
  params: PropTypes.object,
  retry: PropTypes.func,
  onFiltersFormSubmit: PropTypes.func,
  disabledExportAll: boolean,
};
